// Handles commentary generation and preview playback for library host voice customization

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { analyticsService } from '../../../services/analyticsService';
import { useMedia } from '../../../hooks/useMedia';
import { generateCommentary, synthesizeSpeech } from '../../../transport/api';
import type { PlaybackPoppyState } from '../../../components/avatars/Poppy';
import {
  constructVoiceName,
  createLogger,
  normalizeVoiceSettings,
} from '@airhead-games/shared/utilities';
import type {
  UserAuthFields,
  UserPreferences,
} from '@airhead-games/shared/types';

const logger = createLogger('useLibraryPoppyVoicePreview');

interface UseLibraryPoppyVoicePreviewArgs {
  user?: UserAuthFields | null;
  userPreferences: UserPreferences;
  onRequireLogin: () => void;
}

interface UseLibraryPoppyVoicePreviewResult {
  isPreviewPlaying: boolean;
  isGeneratingCommentary: boolean;
  previewCommentary: string;
  libraryPoppyPlaybackState: PlaybackPoppyState | undefined;
}

export function useLibraryPoppyVoicePreview({
  user,
  userPreferences,
  onRequireLogin,
}: UseLibraryPoppyVoicePreviewArgs): UseLibraryPoppyVoicePreviewResult {
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [previewCommentary, setPreviewCommentary] = useState('');
  const [isGeneratingCommentary, setIsGeneratingCommentary] = useState(false);
  const waitingForSpeechRef = useRef(false);
  const prevIsSpeakingRef = useRef(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const latestRequestIdRef = useRef(0);
  const { stopSpeechPlayback, playSpeechBuffer, isSpeaking, setIsMuted } =
    useMedia();

  const generateAndSetCommentary = useCallback(async (): Promise<string> => {
    try {
      const requestContext =
        "We're not playing a game right now, the player just wants to listen to your voice. So introduce yourself as Poppy, the game show host for Airhead Games, and tell the person you are looking forward to playing games with them. Make sure your unique personality style is on display, but keep your comments brief with 2 sentences at the maximum.";
      const commentaryRes = await generateCommentary({
        requestContext,
        userPreferences,
      });
      if (commentaryRes.success && commentaryRes.data?.text) {
        setPreviewCommentary(commentaryRes.data.text);
        return commentaryRes.data.text;
      }
      logger.error('Failed to generate commentary:', commentaryRes);
    } catch (error) {
      logger.error('Commentary generation failed:', error);
    }
    setPreviewCommentary('');
    return '';
  }, [userPreferences]);

  useEffect(() => {
    if (!user) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      setPreviewCommentary('');
      setIsGeneratingCommentary(false);
      return;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setIsGeneratingCommentary(true);
    debounceTimerRef.current = setTimeout(async () => {
      const currentRequestId = ++latestRequestIdRef.current;
      try {
        await generateAndSetCommentary();
      } finally {
        if (latestRequestIdRef.current === currentRequestId) {
          setIsGeneratingCommentary(false);
        }
      }
    }, 1200);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [generateAndSetCommentary, user]);

  useEffect(() => {
    if (isPreviewPlaying && waitingForSpeechRef.current && isSpeaking) {
      waitingForSpeechRef.current = false;
    }
    if (
      isPreviewPlaying &&
      !waitingForSpeechRef.current &&
      prevIsSpeakingRef.current &&
      !isSpeaking
    ) {
      setIsPreviewPlaying(false);
    }
    prevIsSpeakingRef.current = isSpeaking;
  }, [isSpeaking, isPreviewPlaying]);

  const handlePreviewToggle = useCallback(async () => {
    if (!user) {
      onRequireLogin();
      return;
    }

    if (isPreviewPlaying || isSpeaking) {
      stopSpeechPlayback();
      setIsPreviewPlaying(false);
      return;
    }
    if (!previewCommentary) return;
    analyticsService.trackButtonClick('Preview Voice', {
      link_location: 'library_poppy',
      accent: userPreferences.accent,
      gender: userPreferences.hostStyle.gender,
      personality: userPreferences.hostStyle.personality,
    });
    stopSpeechPlayback();
    setIsPreviewPlaying(true);
    waitingForSpeechRef.current = true;
    try {
      const voiceCode = constructVoiceName(
        normalizeVoiceSettings(userPreferences)
      );
      const arrayBuffer = await synthesizeSpeech({
        text: previewCommentary,
        voiceCode,
      });
      await playSpeechBuffer(new Uint8Array(arrayBuffer));
      setIsMuted(false);
    } catch (error) {
      logger.error('Preview speech failed:', error);
      setIsPreviewPlaying(false);
      waitingForSpeechRef.current = false;
    }
  }, [
    isPreviewPlaying,
    isSpeaking,
    onRequireLogin,
    previewCommentary,
    stopSpeechPlayback,
    userPreferences,
    playSpeechBuffer,
    setIsMuted,
    user,
  ]);

  const libraryPoppyPlaybackState = useMemo(
    () =>
      !isGeneratingCommentary && (!!previewCommentary || !user)
        ? {
            isPlaying: isPreviewPlaying,
            onToggle: handlePreviewToggle,
          }
        : undefined,
    [
      handlePreviewToggle,
      isGeneratingCommentary,
      isPreviewPlaying,
      previewCommentary,
      user,
    ]
  );

  return {
    isPreviewPlaying,
    isGeneratingCommentary,
    previewCommentary,
    libraryPoppyPlaybackState,
  };
}
